import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
} from '@tanstack/react-query'
import { useOrpc, type OrpcClient } from '@/hooks/useClient'

/**
 * Data for the Population Health dashboard (PD-1774) from the PD-1715 endpoints
 * (docs/POPULATION.md). Each section has its own query so the sections load and fail
 * independently; all of them take the same period, comparison and filters.
 */

export type PopulationQuery = Parameters<OrpcClient['populationSummary']>[0]
export type Summary = Awaited<ReturnType<OrpcClient['populationSummary']>>
export type PopulationWindow = Summary['window']
export type FamiliesResult = Awaited<ReturnType<OrpcClient['populationFamilies']>>
export type FamilyGroup = FamiliesResult['groups'][number]
export type MeasuresResult = Awaited<ReturnType<OrpcClient['populationMeasures']>>
export type Measure = MeasuresResult['measures'][number]
export type TrendResult = Awaited<ReturnType<OrpcClient['populationTrend']>>
export type TrendPoint = TrendResult['points'][number]
export type AgingBucket = TrendResult['aging'][number]
export type ProvidersResult = Awaited<ReturnType<OrpcClient['populationProviders']>>
export type CohortInput = Parameters<OrpcClient['populationMeasurePatients']>[0]
export type CohortResult = Awaited<ReturnType<OrpcClient['populationMeasurePatients']>>
export type CohortPatient = CohortResult['data'][number]
export type CohortFinding = CohortPatient['inclusionReasons'][number]

/** Which cohort the drawer shows: one measure, or one family group. */
export type CohortTarget = {
  type: 'measure' | 'family'
  slug: string
}

/** Trend scope: all gaps, one family group or one measure (never both). */
export type TrendScope = { groupSlug?: string; measureSlug?: string }

const COHORT_PAGE_SIZE = 50

export function usePopulationSummary(query: PopulationQuery) {
  const orpc = useOrpc()
  return useQuery(
    orpc.populationSummary.queryOptions({ input: query, placeholderData: keepPreviousData }),
  )
}

export function usePopulationFamilies(query: PopulationQuery) {
  const orpc = useOrpc()
  return useQuery(
    orpc.populationFamilies.queryOptions({ input: query, placeholderData: keepPreviousData }),
  )
}

export function usePopulationMeasures(query: PopulationQuery) {
  const orpc = useOrpc()
  return useQuery(
    orpc.populationMeasures.queryOptions({ input: query, placeholderData: keepPreviousData }),
  )
}

export function usePopulationTrend(query: PopulationQuery, scope: TrendScope = {}) {
  const orpc = useOrpc()
  return useQuery(
    orpc.populationTrend.queryOptions({
      input: { ...query, ...scope },
      placeholderData: keepPreviousData,
    }),
  )
}

/** One trend per family group, for the cards' sparklines: the slices only. */
export function useFamilyTrends(query: PopulationQuery, groupSlugs: string[]) {
  const orpc = useOrpc()
  return useQueries({
    queries: groupSlugs.map((groupSlug) =>
      orpc.populationTrend.queryOptions({ input: { ...query, groupSlug, pointsOnly: true } }),
    ),
  })
}

export function usePopulationProviders(query: PopulationQuery) {
  const orpc = useOrpc()
  return useQuery(
    orpc.populationProviders.queryOptions({ input: query, placeholderData: keepPreviousData }),
  )
}

/**
 * The Provider filter's options: every attributed provider for the period, with no filters
 * applied, so choosing a provider doesn't shrink the list to that provider.
 */
export function useProviderOptions({ period, comparison, start, end }: PopulationQuery) {
  const orpc = useOrpc()
  return useQuery(
    orpc.populationProviders.queryOptions({ input: { period, comparison, start, end } }),
  )
}

export function useStateOptions() {
  const orpc = useOrpc()
  return useQuery(orpc.populationStates.queryOptions())
}

/** Hands the browser a file to save. */
function download(filename: string, contents: string, type: string) {
  const url = URL.createObjectURL(new Blob([contents], { type }))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

/**
 * Export cohort (PD-1775): the whole cohort as CSV, downloaded. The server records a download
 * in each exported patient's access history.
 */
export function useCohortExport() {
  const orpc = useOrpc()
  return useMutation(
    orpc.populationCohortExport.mutationOptions({
      onSuccess: ({ filename, csv }) => download(filename, csv, 'text/csv;charset=utf-8'),
    }),
  )
}

/** The drawer's cohort, one page at a time until `page.total` rows are loaded. */
export function useCohort(
  target: CohortTarget | null,
  filters: Omit<CohortInput, 'slug' | 'page'>,
) {
  const orpc = useOrpc()
  const procedure =
    target?.type === 'family' ? orpc.populationFamilyPatients : orpc.populationMeasurePatients
  return useInfiniteQuery(
    procedure.infiniteOptions({
      input: (offset: number) => ({
        ...filters,
        slug: target?.slug ?? '',
        page: { offset, count: COHORT_PAGE_SIZE, limit: offset + COHORT_PAGE_SIZE },
      }),
      initialPageParam: 0,
      getNextPageParam: (last, pages) => {
        const loaded = pages.reduce((sum, page) => sum + page.data.length, 0)
        return loaded < last.page.total ? loaded : undefined
      },
      enabled: target !== null,
      placeholderData: keepPreviousData,
    }),
  )
}
